import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { formatCurrency } from 'src/utils/numberFormatter';
import { ExternalPaymentMethodType } from 'src/WebApiController';

import { RetailSaleFormValues } from '../types';
import * as styles from './ExternalPaymentSection.css';

/**
 * Maps each ExternalPaymentMethodType numeric value (as string key) to its
 * human-readable label. Used as `valueOptionsContent` for PosSelect.
 */
const EXTERNAL_PAYMENT_METHOD_LABELS: Record<string, string> = {
  [String(ExternalPaymentMethodType.CreditCard)]: 'Credit Card',
  [String(ExternalPaymentMethodType.Wire)]: 'Wire',
  [String(ExternalPaymentMethodType.ACH)]: 'ACH',
  [String(ExternalPaymentMethodType.Check)]: 'Check',
  [String(ExternalPaymentMethodType.CustomerCredit)]: 'Customer Credit',
  [String(ExternalPaymentMethodType.Cash)]: 'Cash',
  [String(ExternalPaymentMethodType.Other)]: 'Other',
};

/**
 * Returns the adaptive note placeholder based on the selected payment method.
 */
const getNotePlaceholder = (
  methodType: ExternalPaymentMethodType | null | undefined
): string => {
  switch (methodType) {
    case ExternalPaymentMethodType.CreditCard:
      return 'Card reference or last 4 digits';
    case ExternalPaymentMethodType.Wire:
      return 'Wire reference number';
    case ExternalPaymentMethodType.ACH:
      return 'ACH confirmation number';
    case ExternalPaymentMethodType.Check:
      return 'Check number';
    case ExternalPaymentMethodType.CustomerCredit:
      return 'Credit reference or notes';
    case ExternalPaymentMethodType.Cash:
      return 'Payment notes (optional)';
    case ExternalPaymentMethodType.Other:
      return 'Describe the payment method or reference';
    default:
      return 'Payment method or notes (optional)…';
  }
};

/**
 * Controlled props mode — used when the parent manages state via useState
 * (e.g. MarketplacePaymentDialog which does not use react-hook-form).
 */
export type ExternalPaymentSectionControlledProps = {
  methodType: ExternalPaymentMethodType | undefined;
  note: string;
  date: string;
  currencyCode: string;
  methodTypeError?: string;
  noteError?: string;
  dateError?: string;
  onMethodTypeChange: (value: ExternalPaymentMethodType | undefined) => void;
  onNoteChange: (value: string) => void;
  onDateChange: (value: string) => void;
};

type ExternalPaymentSectionProps = {
  disabled: boolean;
  /**
   * The total sale amount (proceeds + line items). The external payment amount
   * is locked to this value in Step 1; partial payment support comes in Step 2.
   */
  totalAmount: number;
  /**
   * Optional controlled props. When provided the component operates in
   * "controlled mode" and does NOT call useFormContext. This allows the
   * component to be used outside of a react-hook-form FormProvider.
   */
  controlled?: ExternalPaymentSectionControlledProps;
};

/**
 * Renders the sub-form shown when the broker selects "Paid Externally".
 * Collects:
 * - Payment method type (required) — rendered as a dropdown
 * - Note (optional, max 500 chars) — placeholder adapts to selected method
 * - Amount (read-only, locked to sale total — partial payments via PartialPaymentInput)
 * - Date incurred (defaults to today, cannot be future)
 *
 * Supports two modes:
 * 1. **Form-context mode** (default): reads/writes via useFormContext<RetailSaleFormValues>.
 *    Used by RetailSaleCheckoutStep (react-hook-form FormProvider is present).
 * 2. **Controlled mode**: pass `controlled` prop. Used by MarketplacePaymentDialog
 *    which manages external-payment fields via useState.
 */
export const ExternalPaymentSection = ({
  disabled,
  totalAmount,
  controlled,
}: ExternalPaymentSectionProps) => {
  // Call hooks unconditionally to satisfy the Rules of Hooks.
  // When `controlled` is provided, we ignore the form-context values.
  const formContext = useFormContext<RetailSaleFormValues>();

  const isControlled = controlled != null;

  // Form-context values (only used when NOT in controlled mode)
  const fcMethodType = formContext?.watch('externalPaymentMethodType');
  const fcDate = formContext?.watch('externalPaymentDate');
  const fcNote = formContext?.watch('externalPaymentNote');
  const fcCurrencyCode = formContext?.watch('currencyCode') ?? 'USD';
  const fcMethodTypeError =
    formContext?.formState?.errors?.externalPaymentMethodType?.message;
  const fcDateError =
    formContext?.formState?.errors?.externalPaymentDate?.message;
  const fcNoteError =
    formContext?.formState?.errors?.externalPaymentNote?.message;

  // Form-context mode: keep externalPaymentAmount in sync with the sale total.
  useEffect(() => {
    if (!isControlled && formContext) {
      formContext.setValue('externalPaymentAmount', totalAmount, {
        shouldValidate: false,
      });
    }
  }, [totalAmount, formContext, isControlled]);

  // Resolved values — pick controlled or form-context
  const methodType = isControlled ? controlled.methodType : fcMethodType;
  const dateStr = isControlled ? controlled.date : fcDate;
  const note = isControlled ? controlled.note : (fcNote ?? '');
  const currencyCode = isControlled ? controlled.currencyCode : fcCurrencyCode;
  const methodTypeError = isControlled
    ? controlled.methodTypeError
    : fcMethodTypeError;
  const dateError = isControlled ? controlled.dateError : fcDateError;
  const noteError = isControlled ? controlled.noteError : fcNoteError;

  const notePlaceholder = getNotePlaceholder(methodType);

  // Hoist today's date so it is not recalculated on every render.
  const maxDate = useMemo(() => new Date(), []);

  // Convert the stored ISO date string to a Date object for DatePickerInput.
  const dateValue = useMemo(
    () => (dateStr ? new Date(dateStr) : undefined),
    [dateStr]
  );

  const handleMethodTypeChange = (value: string) => {
    const parsed =
      value === ''
        ? undefined
        : (value as unknown as ExternalPaymentMethodType);
    if (isControlled) {
      controlled.onMethodTypeChange(parsed);
    } else {
      formContext?.setValue(
        'externalPaymentMethodType',
        parsed === undefined ? null : parsed,
        { shouldValidate: true }
      );
    }
  };

  const handleNoteChange = (value: string) => {
    if (isControlled) {
      controlled.onNoteChange(value);
    } else {
      formContext?.setValue('externalPaymentNote', value || null, {
        shouldValidate: true,
      });
    }
  };

  const handleDateChange = (date: Date) => {
    const isoDate = date.toISOString().split('T')[0];
    if (isControlled) {
      controlled.onDateChange(isoDate);
    } else {
      formContext?.setValue('externalPaymentDate', isoDate, {
        shouldValidate: true,
      });
    }
  };

  return (
    <div className={styles.sectionContainer}>
      {/* Payment method type dropdown */}
      <PosFormField label="Payment Method Type *" errors={methodTypeError}>
        <PosSelect
          value={methodType != null ? String(methodType) : undefined}
          placeholderText="Select payment method"
          valueOptionsContent={EXTERNAL_PAYMENT_METHOD_LABELS}
          sortMode="none"
          disabled={disabled}
          hasErrors={!!methodTypeError}
          showFullWidth
          onChange={handleMethodTypeChange}
        />
      </PosFormField>

      {/* Note */}
      <PosFormField label="Note" errors={noteError}>
        <PosTextArea
          placeholder={notePlaceholder}
          maxLength={500}
          disabled={disabled}
          value={note ?? ''}
          onChange={(e) => handleNoteChange(e.target.value)}
        />
      </PosFormField>

      {/* Amount — read-only, locked to the sale total (partial handled by PartialPaymentInput) */}
      <PosFormField label="Amount">
        <div className={styles.amountRow}>
          <span className={styles.currencyLabel}>{currencyCode}</span>
          <span>{formatCurrency(totalAmount, currencyCode)}</span>
        </div>
      </PosFormField>

      {/* Date incurred */}
      <PosFormField label="Date Incurred *" errors={dateError}>
        <DatePickerInput
          date={dateValue}
          maxDate={maxDate}
          disabled={disabled}
          onDateChange={handleDateChange}
        />
      </PosFormField>
    </div>
  );
};
